// src/services/cloudinary/cloudinary.service.js

/**
 * Cloudinary Service for Frontend Image Upload
 * This service handles image uploads directly from the browser to Cloudinary
 * using unsigned upload presets (no backend needed)
 */

class CloudinaryService {
  constructor() {
    this.cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || "dwrw8jakz";
    this.uploadPreset = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || "burotime_preset"; // You need to create this
    this.apiKey = process.env.REACT_APP_CLOUDINARY_API_KEY || "235363518758691";
    this.uploadUrl = `https://api.cloudinary.com/v1_1/${this.cloudName}/image/upload`;
  }

  /**
   * Upload single image to Cloudinary
   * @param {File} file - Image file to upload
   * @param {Object} options - Upload options (folder, tags, etc.)
   * @returns {Promise<Object>} Upload result with URL and public_id
   */
  async uploadImage(file, options = {}) {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("upload_preset", this.uploadPreset);
      
      // Optional parameters
      if (options.folder) {
        formData.append("folder", options.folder);
      }
      
      if (options.tags) {
        formData.append("tags", options.tags.join(","));
      }

      if (options.publicId) {
        formData.append("public_id", options.publicId);
      }

      // Make upload request
      const response = await fetch(this.uploadUrl, {
        method: "POST",
        body: formData
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }

      const data = await response.json();

      return {
        success: true,
        url: data.secure_url,
        publicId: data.public_id,
        format: data.format,
        width: data.width,
        height: data.height,
        bytes: data.bytes,
        createdAt: data.created_at
      };
    } catch (error) {
      console.error("Cloudinary upload error:", error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Upload multiple images
   * @param {FileList|Array} files - Array of image files
   * @param {Object} options - Upload options
   * @returns {Promise<Array>} Array of upload results
   */
  async uploadMultipleImages(files, options = {}) {
    try {
      const uploadPromises = Array.from(files).map(file => 
        this.uploadImage(file, options)
      );

      const results = await Promise.all(uploadPromises);
      
      const successfulUploads = results.filter(result => result.success);
      const failedUploads = results.filter(result => !result.success);

      return {
        success: failedUploads.length === 0,
        uploaded: successfulUploads,
        failed: failedUploads,
        total: files.length,
        successCount: successfulUploads.length,
        failedCount: failedUploads.length
      };
    } catch (error) {
      console.error("Multiple upload error:", error);
      return {
        success: false,
        error: error.message,
        uploaded: [],
        failed: Array.from(files).map(file => ({ fileName: file.name }))
      };
    }
  }

  /**
   * Delete image from Cloudinary
   * Note: This requires a backend endpoint for security
   * Cloudinary doesn't allow deletion from frontend directly
   * @param {string} publicId - Public ID of the image to delete
   * @returns {Promise<Object>} Deletion result
   */
  async deleteImage(publicId) {
    // This should call your backend API
    console.warn("Image deletion requires backend implementation for security");
    return {
      success: false,
      message: "Deletion must be done through backend API"
    };
  }

  /**
   * Generate optimized image URL
   * @param {string} publicId - Cloudinary public ID
   * @param {Object} transformations - Image transformation options
   * @returns {string} Optimized image URL
   */
  getOptimizedUrl(publicId, transformations = {}) {
    const {
      width = "auto",
      height = "auto",
      crop = "fill",
      quality = "auto",
      format = "auto",
      gravity = "auto"
    } = transformations;

    return `https://res.cloudinary.com/${this.cloudName}/image/upload/w_${width},h_${height},c_${crop},q_${quality},f_${format},g_${gravity}/${publicId}`;
  }

  /**
   * Generate thumbnail URL
   * @param {string} publicId - Cloudinary public ID
   * @param {number} size - Thumbnail size (default: 150)
   * @returns {string} Thumbnail URL
   */
  getThumbnailUrl(publicId, size = 150) {
    return this.getOptimizedUrl(publicId, {
      width: size,
      height: size,
      crop: "fill",
      quality: "auto",
      format: "auto"
    });
  }

  /**
   * Generate responsive image srcset
   * @param {string} publicId - Cloudinary public ID
   * @param {Array} widths - Array of widths for srcset
   * @returns {string} srcset string
   */
  getResponsiveSrcSet(publicId, widths = [320, 640, 768, 1024, 1280, 1536]) {
    return widths
      .map(width => {
        const url = this.getOptimizedUrl(publicId, { width });
        return `${url} ${width}w`;
      })
      .join(", ");
  }

  /**
   * Validate image file
   * @param {File} file - File to validate
   * @param {Object} options - Validation options
   * @returns {Object} Validation result
   */
  validateImage(file, options = {}) {
    const {
      maxSize = 5 * 1024 * 1024, // 5MB default
      allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/jpg"],
      minWidth = 0,
      minHeight = 0
    } = options;

    // Check file size
    if (file.size > maxSize) {
      return {
        valid: false,
        error: `File size exceeds ${maxSize / (1024 * 1024)}MB limit`
      };
    }

    // Check file type
    if (!allowedTypes.includes(file.type)) {
      return {
        valid: false,
        error: `File type ${file.type} is not allowed. Allowed types: ${allowedTypes.join(", ")}`
      };
    }

    // Check dimensions if needed
    if (minWidth > 0 || minHeight > 0) {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          if (img.width < minWidth || img.height < minHeight) {
            resolve({
              valid: false,
              error: `Image dimensions must be at least ${minWidth}x${minHeight}px`
            });
          } else {
            resolve({ valid: true });
          }
        };
        img.onerror = () => {
          resolve({
            valid: false,
            error: "Could not load image"
          });
        };
        img.src = URL.createObjectURL(file);
      });
    }

    return { valid: true };
  }

  /**
   * Extract public ID from Cloudinary URL
   * @param {string} url - Cloudinary URL
   * @returns {string} Public ID
   */
  extractPublicId(url) {
    const regex = /\/v\d+\/(.+)\.\w+$/;
    const match = url.match(regex);
    return match ? match[1] : null;
  }

  /**
   * Generate placeholder/blur image
   * @param {string} publicId - Cloudinary public ID
   * @returns {string} Blurred placeholder URL
   */
  getPlaceholderUrl(publicId) {
    return `https://res.cloudinary.com/${this.cloudName}/image/upload/w_50,h_50,e_blur:1000,q_1,f_auto/${publicId}`;
  }
}

export default new CloudinaryService();

// Export additional helper functions
export const UPLOAD_FOLDERS = {
  PRODUCTS: "products",
  CATEGORIES: "categories",
  USERS: "users",
  BANNERS: "banners",
  BLOG: "blog"
};

export const IMAGE_TRANSFORMATIONS = {
  PRODUCT_MAIN: { width: 800, height: 800, crop: "fill" },
  PRODUCT_THUMBNAIL: { width: 200, height: 200, crop: "fill" },
  CATEGORY_CARD: { width: 400, height: 300, crop: "fill" },
  BANNER_DESKTOP: { width: 1920, height: 600, crop: "fill" },
  BANNER_MOBILE: { width: 768, height: 400, crop: "fill" },
  USER_AVATAR: { width: 150, height: 150, crop: "fill", gravity: "face" }
};